import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { RefreshCw, Activity, FileText, Database, AlertCircle, AlertTriangle, Info, ChevronDown, ChevronRight, Server, Clock, HardDrive } from 'lucide-react';

// Build info from Vite environment (set at Docker build time)
const WEB_BUILD_INFO = {
  buildNumber: import.meta.env.VITE_BUILD_NUMBER || 'local',
  commit: import.meta.env.VITE_BUILD_COMMIT || 'local',
  buildDate: import.meta.env.VITE_BUILD_DATE || new Date().toISOString(),
};

// Types for deployment info
interface DeploymentInfo {
  api: {
    version: string;
    commit: string;
    buildNumber: string;
    buildDate: string;
    nodeEnv: string;
    uptime: number;
    memoryUsage: {
      heapUsed: number;
      heapTotal: number;
      rss: number;
    };
  };
  database: {
    connected: boolean;
    latestMigration: {
      id: number;
      hash: string;
      appliedAt: string;
    } | null;
    totalMigrations: number;
    recentMigrations: Array<{
      id: number;
      hash: string;
      appliedAt: string;
    }>;
    error?: string;
  };
}

// Types
interface AdminStatus {
  sources: Array<{
    id: string;
    name: string;
    type: 'google' | 'microsoft' | 'ics';
    memberId: string;
    memberName: string;
    lastSynced: string | null;
    hasSyncToken: boolean;
    isActive: boolean;
  }>;
  keepSources: Array<{
    id: string;
    name: string;
    syncEnabled: boolean;
    lastSynced: string | null;
    lastSyncError: string | null;
  }>;
  users: Array<{
    id: string;
    email: string;
    name: string;
    hasGoogle: boolean;
    hasMicrosoft: boolean;
    hasKeep: boolean;
  }>;
  invitations: Array<{
    id: string;
    email: string;
    status: string;
    expiresAt: string;
    memberName: string;
  }>;
}

interface ErrorLog {
  id: string;
  timestamp: string;
  level: 'error' | 'warn' | 'info';
  message: string;
  context: Record<string, unknown>;
  userId: string | null;
  endpoint: string | null;
  errorType: string | null;
}

interface LogsResponse {
  logs: ErrorLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface AdminEvent {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  location: string | null;
  sourceId: string;
  sourceName: string;
  sourceType: 'google' | 'microsoft' | 'ics';
  memberId: string;
  memberName: string;
  memberColor: string;
  createdAt: string;
}

interface EventsResponse {
  events: AdminEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface AdminList {
  id: string;
  name: string;
  type: 'groceries' | 'packing' | 'watch' | 'custom';
  color: string | null;
  itemCount: number;
  checkedCount: number;
  syncEnabled: boolean;
  lastSynced: string | null;
  syncSource: string | null;
  keepMapping: {
    keepListId: string;
    keepName: string;
    syncEnabled: boolean;
    lastSynced: string | null;
    lastSyncError: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

type AdminTab = 'deployment' | 'status' | 'logs' | 'data';

export function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('deployment');

  const tabs: { id: AdminTab; label: string; icon: React.ElementType }[] = [
    { id: 'deployment', label: 'Deployment', icon: Server },
    { id: 'status', label: 'Status', icon: Activity },
    { id: 'logs', label: 'Logs', icon: FileText },
    { id: 'data', label: 'Data', icon: Database },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Admin Dashboard</h1>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-teal border-teal'
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'deployment' && <DeploymentTab />}
      {activeTab === 'status' && <StatusTab />}
      {activeTab === 'logs' && <LogsTab />}
      {activeTab === 'data' && <DataTab />}
    </div>
  );
}

// Helper function to format bytes
function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let value = bytes;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
}

// Helper function to format uptime
function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

// Deployment Tab - shows version, commit, docker, migration info
function DeploymentTab() {
  const { data, isLoading, error, refetch } = useQuery<DeploymentInfo>({
    queryKey: ['admin-deployment'],
    queryFn: () => api.get<DeploymentInfo>('/admin/deployment'),
    refetchInterval: 30000, // Auto-refresh every 30s
  });

  if (isLoading) {
    return <div className="text-text-secondary">Loading deployment info...</div>;
  }

  if (error) {
    return (
      <div className="bg-coral/10 border border-coral rounded-lg p-4 text-coral">
        Failed to load deployment info. Check console for details.
      </div>
    );
  }

  const commitShort = (commit: string) => commit.substring(0, 8);

  return (
    <div className="space-y-6">
      {/* Refresh button */}
      <div className="flex justify-end">
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Web Frontend Info */}
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-teal" />
          Web Frontend
        </h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Build Number</div>
              <div className="text-lg font-mono font-semibold text-teal">#{WEB_BUILD_INFO.buildNumber}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Commit</div>
              <div className="font-mono text-text-primary">{commitShort(WEB_BUILD_INFO.commit)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Build Date</div>
              <div className="text-text-secondary">
                {new Date(WEB_BUILD_INFO.buildDate).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-xs text-gray-500 uppercase mb-1">Docker Image</div>
            <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
              my-family-calendar:web-{WEB_BUILD_INFO.buildNumber}
            </code>
          </div>
        </div>
      </section>

      {/* API Backend Info */}
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
          <Server className="w-5 h-5 text-sage" />
          API Backend
        </h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Build Number</div>
              <div className="text-lg font-mono font-semibold text-sage">#{data?.api.buildNumber || 'N/A'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Commit</div>
              <div className="font-mono text-text-primary">{commitShort(data?.api.commit || 'N/A')}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Build Date</div>
              <div className="text-text-secondary">
                {data?.api.buildDate ? new Date(data.api.buildDate).toLocaleString() : 'N/A'}
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Uptime</div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-text-muted" />
                <span className="text-text-secondary">{formatUptime(data?.api.uptime || 0)}</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Memory (Heap Used)</div>
              <div className="text-text-secondary">
                {formatBytes(data?.api.memoryUsage?.heapUsed || 0)} / {formatBytes(data?.api.memoryUsage?.heapTotal || 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Environment</div>
              <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                data?.api.nodeEnv === 'production' ? 'bg-sage/20 text-sage' : 'bg-amber/20 text-amber-dark'
              }`}>
                {data?.api.nodeEnv || 'N/A'}
              </span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-xs text-gray-500 uppercase mb-1">Docker Image</div>
            <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
              my-family-calendar:api-{data?.api.buildNumber || 'N/A'}
            </code>
          </div>
        </div>
      </section>

      {/* Database Info */}
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
          <Database className="w-5 h-5 text-amber" />
          Database
        </h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Connection Status</div>
              <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                data?.database.connected ? 'bg-sage/20 text-sage' : 'bg-coral/20 text-coral'
              }`}>
                {data?.database.connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Total Migrations</div>
              <div className="text-lg font-semibold text-text-primary">{data?.database.totalMigrations || 0}</div>
            </div>
          </div>

          {data?.database.latestMigration && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs text-gray-500 uppercase mb-2">Latest Migration</div>
              <div className="bg-gray-50 rounded p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono text-sm text-text-primary">#{data.database.latestMigration.id}</div>
                    <div className="text-xs text-gray-500 font-mono mt-1">{data.database.latestMigration.hash}</div>
                  </div>
                  <div className="text-xs text-text-secondary">
                    {new Date(data.database.latestMigration.appliedAt).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {data?.database.recentMigrations && data.database.recentMigrations.length > 1 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs text-gray-500 uppercase mb-2">Recent Migrations</div>
              <div className="space-y-2">
                {data.database.recentMigrations.slice(1, 5).map((m) => (
                  <div key={m.id} className="flex justify-between items-center text-sm bg-gray-50 rounded px-3 py-2">
                    <span className="font-mono">#{m.id} - {m.hash}</span>
                    <span className="text-xs text-text-secondary">{new Date(m.appliedAt).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data?.database.error && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="bg-coral/10 border border-coral/30 rounded p-3 text-sm text-coral">
                {data.database.error}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Version Match Check */}
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3">Version Check</h2>
        <div className={`rounded-lg border p-4 ${
          WEB_BUILD_INFO.buildNumber === data?.api.buildNumber
            ? 'bg-sage/10 border-sage/30'
            : 'bg-amber/10 border-amber/30'
        }`}>
          {WEB_BUILD_INFO.buildNumber === data?.api.buildNumber ? (
            <div className="flex items-center gap-2 text-sage">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Web and API versions match (Build #{WEB_BUILD_INFO.buildNumber})</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-dark">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">
                Version mismatch: Web #{WEB_BUILD_INFO.buildNumber} vs API #{data?.api.buildNumber || 'N/A'}
              </span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// Status Tab - existing content
function StatusTab() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<AdminStatus>({
    queryKey: ['admin-status'],
    queryFn: () => api.get<AdminStatus>('/admin/status'),
  });

  const syncMutation = useMutation({
    mutationFn: (sourceId: string) => api.post(`/sources/${sourceId}/sync`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-status'] }),
  });

  if (isLoading) {
    return <div className="text-text-secondary">Loading status...</div>;
  }

  if (error) {
    return (
      <div className="bg-coral/10 border border-coral rounded-lg p-4 text-coral">
        Access denied. Admin privileges required.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Calendar Sources */}
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3">Calendar Sources</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Synced</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.sources.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-4 text-center text-gray-500">No calendar sources</td></tr>
              )}
              {data?.sources.map((source) => (
                <tr key={source.id} className={!source.isActive ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="px-4 py-3 text-sm text-text-primary">{source.name}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary capitalize">{source.type}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{source.memberName}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {source.lastSynced ? new Date(source.lastSynced).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                      source.hasSyncToken && source.isActive ? 'bg-sage/20 text-sage' : 'bg-coral/20 text-coral'
                    }`}>
                      {source.hasSyncToken && source.isActive ? 'Healthy' : source.isActive ? 'No Token' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => syncMutation.mutate(source.id)}
                      disabled={syncMutation.isPending || !source.isActive}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-teal text-white text-sm rounded hover:bg-teal-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={`w-3 h-3 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                      Sync
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Keep Sources */}
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3">Google Keep Sources</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enabled</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Synced</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.keepSources.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-4 text-center text-gray-500">No Keep sources</td></tr>
              )}
              {data?.keepSources.map((keep) => (
                <tr key={keep.id} className={keep.lastSyncError ? 'bg-coral/5' : ''}>
                  <td className="px-4 py-3 text-sm text-text-primary">{keep.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                      keep.syncEnabled ? 'bg-sage/20 text-sage' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {keep.syncEnabled ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {keep.lastSynced ? new Date(keep.lastSynced).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-sm text-coral max-w-md truncate" title={keep.lastSyncError || undefined}>
                    {keep.lastSyncError || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Users */}
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3">Users</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Google</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Microsoft</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keep</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.users.map((user) => (
                <tr key={user.id}>
                  <td className="px-4 py-3 text-sm text-text-primary">{user.email}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{user.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={user.hasGoogle ? 'text-sage' : 'text-gray-300'}>{user.hasGoogle ? '\u2713' : '\u2013'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={user.hasMicrosoft ? 'text-sage' : 'text-gray-300'}>{user.hasMicrosoft ? '\u2713' : '\u2013'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={user.hasKeep ? 'text-sage' : 'text-gray-300'}>{user.hasKeep ? '\u2713' : '\u2013'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Invitations */}
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3">Pending Invitations</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {data?.invitations.length === 0 ? (
            <p className="px-4 py-4 text-gray-500 text-center">No pending invitations</p>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data?.invitations.map((invite) => (
                  <tr key={invite.id}>
                    <td className="px-4 py-3 text-sm text-text-primary">{invite.email}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{invite.memberName}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{new Date(invite.expiresAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

// Logs Tab
function LogsTab() {
  const [page, setPage] = useState(1);
  const [levelFilter, setLevelFilter] = useState<'error' | 'warn' | 'info' | ''>('');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<LogsResponse>({
    queryKey: ['admin-logs', page, levelFilter],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (levelFilter) params.set('level', levelFilter);
      return api.get<LogsResponse>(`/admin/logs?${params}`);
    },
  });

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error': return <AlertCircle className="w-4 h-4 text-coral" />;
      case 'warn': return <AlertTriangle className="w-4 h-4 text-amber" />;
      default: return <Info className="w-4 h-4 text-teal" />;
    }
  };

  const getLevelBadge = (level: string) => {
    const colors = {
      error: 'bg-coral/20 text-coral',
      warn: 'bg-amber/20 text-amber-dark',
      info: 'bg-teal/20 text-teal',
    };
    return colors[level as keyof typeof colors] || colors.info;
  };

  if (isLoading) return <div className="text-text-secondary">Loading logs...</div>;
  if (error) return <div className="text-coral">Failed to load logs</div>;

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex gap-4 items-center">
        <label className="text-sm text-text-secondary">Filter by level:</label>
        <select
          value={levelFilter}
          onChange={(e) => { setLevelFilter(e.target.value as typeof levelFilter); setPage(1); }}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm"
        >
          <option value="">All</option>
          <option value="error">Error</option>
          <option value="warn">Warning</option>
          <option value="info">Info</option>
        </select>
        <span className="text-sm text-text-secondary">
          {data?.pagination.total || 0} total logs
        </span>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.logs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No logs found</td></tr>
            )}
            {data?.logs.map((log) => (
              <>
                <tr
                  key={log.id}
                  className={`cursor-pointer hover:bg-gray-50 ${log.level === 'error' ? 'bg-coral/5' : ''}`}
                  onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                >
                  <td className="px-4 py-3">
                    {expandedLogId === log.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getLevelBadge(log.level)}`}>
                      {getLevelIcon(log.level)}
                      {log.level}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-primary max-w-md truncate">{log.message}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary font-mono">{log.endpoint || '-'}</td>
                </tr>
                {expandedLogId === log.id && (
                  <tr key={`${log.id}-expanded`}>
                    <td colSpan={5} className="px-4 py-3 bg-gray-50">
                      <div className="text-sm">
                        <div className="mb-2"><strong>Error Type:</strong> {log.errorType || 'N/A'}</div>
                        <div className="mb-2"><strong>User ID:</strong> {log.userId || 'N/A'}</div>
                        <div><strong>Context:</strong></div>
                        <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                          {JSON.stringify(log.context, null, 2)}
                        </pre>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex justify-between items-center">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 border rounded text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-text-secondary">
            Page {page} of {data.pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
            disabled={page === data.pagination.totalPages}
            className="px-3 py-1.5 border rounded text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// Data Tab
function DataTab() {
  const [dataSection, setDataSection] = useState<'events' | 'lists'>('events');

  return (
    <div className="space-y-4">
      {/* Sub-section Toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setDataSection('events')}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
            dataSection === 'events' ? 'bg-teal text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Events
        </button>
        <button
          onClick={() => setDataSection('lists')}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
            dataSection === 'lists' ? 'bg-teal text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Lists
        </button>
      </div>

      {dataSection === 'events' && <EventsSection />}
      {dataSection === 'lists' && <ListsSection />}
    </div>
  );
}

function EventsSection() {
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery<EventsResponse>({
    queryKey: ['admin-events', page],
    queryFn: () => api.get<EventsResponse>(`/admin/events?page=${page}&limit=25`),
  });

  if (isLoading) return <div className="text-text-secondary">Loading events...</div>;
  if (error) return <div className="text-coral">Failed to load events</div>;

  return (
    <div className="space-y-4">
      <div className="text-sm text-text-secondary">{data?.pagination.total || 0} total events</div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.events.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No events found</td></tr>
            )}
            {data?.events.map((event) => (
              <tr key={event.id}>
                <td className="px-4 py-3 text-sm text-text-primary max-w-xs truncate">{event.title}</td>
                <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                  {event.allDay ? new Date(event.startTime).toLocaleDateString() : new Date(event.startTime).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className="capitalize text-text-secondary">{event.sourceType}</span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="text-text-primary">{event.sourceName}</span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className="inline-flex items-center gap-1.5"
                    style={{ color: event.memberColor }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: event.memberColor }} />
                    {event.memberName}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-text-secondary max-w-xs truncate">{event.location || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex justify-between items-center">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 border rounded text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-text-secondary">
            Page {page} of {data.pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
            disabled={page === data.pagination.totalPages}
            className="px-3 py-1.5 border rounded text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function ListsSection() {
  const { data, isLoading, error } = useQuery<AdminList[]>({
    queryKey: ['admin-lists'],
    queryFn: () => api.get<AdminList[]>('/admin/lists'),
  });

  if (isLoading) return <div className="text-text-secondary">Loading lists...</div>;
  if (error) return <div className="text-coral">Failed to load lists</div>;

  return (
    <div className="space-y-4">
      <div className="text-sm text-text-secondary">{data?.length || 0} total lists</div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keep Sync</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Synced</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sync Error</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No lists found</td></tr>
            )}
            {data?.map((list) => (
              <tr key={list.id} className={list.keepMapping?.lastSyncError ? 'bg-coral/5' : ''}>
                <td className="px-4 py-3 text-sm">
                  <span className="flex items-center gap-2">
                    {list.color && <span className="w-3 h-3 rounded" style={{ backgroundColor: list.color }} />}
                    <span className="text-text-primary">{list.name}</span>
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-text-secondary capitalize">{list.type}</td>
                <td className="px-4 py-3 text-sm text-text-secondary">
                  {list.checkedCount}/{list.itemCount}
                </td>
                <td className="px-4 py-3 text-sm">
                  {list.keepMapping ? (
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                      list.keepMapping.syncEnabled ? 'bg-sage/20 text-sage' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {list.keepMapping.syncEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-text-secondary">
                  {list.keepMapping?.lastSynced
                    ? new Date(list.keepMapping.lastSynced).toLocaleString()
                    : list.lastSynced
                      ? new Date(list.lastSynced).toLocaleString()
                      : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-coral max-w-xs truncate" title={list.keepMapping?.lastSyncError || undefined}>
                  {list.keepMapping?.lastSyncError || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
